import { ClientStorage } from '@amzn/ring-cs-chatbot-widget';

const safeWindow = typeof window !== 'undefined' ? window : undefined;
const safeLocalStorage = typeof localStorage !== 'undefined' ? localStorage : undefined;

export class AbstractStorage {
    getItem = (key: string): string | null => {
        throw Error('Abstract base class');
    }
    setItem = (key: string, value: string): void => {
        throw Error('Abstract base class');
    };
    removeItem = (key: string): void => {
        throw Error('Abstract base class');
    };
    onStorage = (handler: (key: string, value?: string) => void): (() => void) => {
        throw Error('Abstract base class');
    };
}

class MemoryStorage extends AbstractStorage {
    private readonly storage: Record<string, string>

    constructor () {
        super();
        this.storage = {};
    }

    getItem = (key: string): string | null => {
        return this.storage[key] ?? null;
    };
    setItem = (key: string, value: string): void => {
        this.storage[key] = value;
    };
    removeItem = (key: string): void => {
        delete this.storage[key];
    };
    onStorage = (handler: (key: string, value?: string) => void) => {
        // no storage events for memory storage
        return () => { };
    };
}

class LocalStorage extends AbstractStorage {
    getItem = (key: string): string | null =>
        safeLocalStorage!.getItem(key);
    setItem = (key: string, value: string): void =>
        safeLocalStorage!.setItem(key, value);
    removeItem = (key: string): void =>
        safeLocalStorage!.removeItem(key);
    onStorage = (handler: (key: string, value?: string) => void) => {
        const storageHandler = (ev: StorageEvent) => {
            if (ev.key) {
                handler(ev.key, ev.newValue || undefined);
            }
        };
        safeWindow!.addEventListener('storage', storageHandler);
        return () => safeWindow!.removeEventListener('storage', storageHandler);
    };
}

export const MEM_STORAGE = new MemoryStorage();

const createStorage = () => {
    if (!safeLocalStorage) {
        return MEM_STORAGE;
    }

    const queryString = safeWindow?.location.search;
    const urlParams = new URLSearchParams(queryString);
    const clientStorage = urlParams.get('clientStorage');

    return clientStorage === ClientStorage.MEMORY ? MEM_STORAGE : new LocalStorage();
};

export const STORAGE = createStorage();
